import Highcharts from 'highcharts';
import HighchartsBoost from 'highcharts/modules/boost';
import Heatmap from 'highcharts/modules/heatmap';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { EMITTER_CONSTANTS } from '@/shared/config';
import { useEmitter, useUpdateEffect } from '@/shared/lib/customHooks';
import emitter from '@/shared/lib/eventEmitter';
import { BEAT_HR_TAB_ID, useReportStore } from '@/entities/report';
import { calculateReviewedStatus } from '@/widgets/beats/ui/reviewBeatsHelper';
import {
  CHART_INFO,
  formatDataChart,
  formatEmptyPoint,
  formatNumber,
  formatReviewedPoint,
  formatSelectedPoint,
  formatUnselectedPoint,
  numberWithCommas,
} from './handler';
import type { HeatMapChartProps } from './index';

HighchartsBoost(Highcharts);
Heatmap(Highcharts);

/**
 * Owns the RR interval heat map, drawing the Highcharts instance and keeping cell selection and reviewed styling in sync. Wires marquee, click, ctrl-click and shift-click selection plus an ESC clear, redraws on emitter events and on beat type changes, restyles points as reviewed, unreviewed or hidden whenever pending review edits change, and destroys the chart on unmount.
 *
 * @param props - Chart props carrying the activeButton beat type.
 * @returns An object carrying the isLoading flag.
 */
const useHeatMapChart = ({ activeButton }: HeatMapChartProps) => {
  const dataHeatmapChart = useReportStore((s) => s.dataHeatmapChart);
  /**
   * Reads the RR heat map chart data straight off the report store. Uses a getState snapshot so effects see the current data without adding a subscription.
   *
   * @returns The current heat map chart data keyed by beat type.
   */
  const getDataHeatmapChart = () => useReportStore.getState().dataHeatmapChart;
  const setListSelectedCells = useReportStore((s) => s.actions.setListSelectedCells);
  /**
   * Reads the selected heat map cells straight off the report store. Uses a getState snapshot so chart callbacks see the current selection without adding a subscription.
   *
   * @returns The currently selected heat map cells.
   */
  const getListSelectedCells = () => useReportStore.getState().listSelectedCells;
  const isHideReviewedCell = useReportStore((s) => s.isHideReviewedCell);
  /**
   * Reads the hide-reviewed-cells flag straight off the report store. Uses a getState snapshot so chart callbacks see the current value without adding a subscription.
   *
   * @returns True when reviewed heat map cells are currently hidden.
   */
  const getIsHideReviewedCell = () => useReportStore.getState().isHideReviewedCell;
  const [isLoading, setIsLoading] = useState(true);
  const bulkReviewedBeats = useReportStore((s) => s.bulkReviewedBeats);
  const reviewedBeatsChanges = useReportStore((s) => s.reviewedBeatsChanges);
  const resetBulkReviewedBeats = useReportStore((s) => s.resetBulkReviewedBeats);
  /**
   * Reads the pending reset entries for bulk beat review straight off the report store. Uses a getState snapshot so chart callbacks see the current value without adding a subscription.
   *
   * @returns The current resetBulkReviewedBeats list.
   */
  const getResetBulkReviewedBeats = () => useReportStore.getState().resetBulkReviewedBeats;
  /**
   * Reads the pending per-beat review changes straight off the report store. Uses a getState snapshot so chart callbacks see the current value without adding a subscription.
   *
   * @returns The current reviewedBeatsChanges list.
   */
  const getReviewedBeatsChanges = () => useReportStore.getState().reviewedBeatsChanges;
  /**
   * Reads the pending bulk beat-review entries straight off the report store. Bypasses the hook subscription by calling getState so chart callbacks see the current value without re-rendering.
   *
   * @returns The current bulkReviewedBeats list.
   */
  const getBulkReviewedBeats = () => useReportStore.getState().bulkReviewedBeats;

  const currentDataHeatmapChart = (dataHeatmapChart as any)[activeButton];

  const isLoadedChart = useRef(false);
  const chartRef = useRef<any>(null);

  /**
   * Replaces the heat map selection with every non-empty cell inside a marquee. Collects the in-area points with their counts and templates, restyles the cells that fell out of the selection, clears the whole selection when the marquee caught nothing, and schedules a redraw when any point styling changed.
   *
   * @param area - Marquee bounds given as xMin, xMax, yMin and yMax.
   * @param chart - Highcharts chart holding the heat map series.
   * @returns Nothing.
   */
  const handleNormalSelectPoint = (area: any = {}, chart: any) => {
    setListSelectedCells((listSelectedCells: any) => {
      const { data } = chart.series[0];
      const newListSelectedCells: any[] = [];
      let isForceRedraw = false;
      let hasCellSelected = false;
      const listCellUnSelected: any[] = [];

      _.forEach(data, (item) => {
        const isInList = _.some(listSelectedCells, { id: item.id });
        const isInArea =
          item.x >= area.xMin &&
          item.x <= area.xMax &&
          item.y >= area.yMin &&
          item.y <= area.yMax &&
          !item.isEmpty;

        if (isInArea) {
          hasCellSelected = true;
          newListSelectedCells.push({
            index: item.index,
            id: item.id,
            value: item.value,
            reviewedCount: item.reviewedCount || 0,
            originalReviewedCount: item.originalReviewedCount || 0,
            templates: item.templates || [],
          });
          if (!isInList) {
            isForceRedraw = true;
            item.update(formatSelectedPoint(), false);
          }
          return;
        }

        if (isInList) {
          listCellUnSelected.push(item);
        }
      });

      if (hasCellSelected) {
        _.forEach(listCellUnSelected, (item) => {
          isForceRedraw = true;
          item.update(formatUnselectedPoint(), false);
        });
      } else {
        _.forEach(listSelectedCells, (item) => {
          isForceRedraw = true;
          const point = data[item.index];
          point.update(formatUnselectedPoint(), false);
        });
      }

      if (isForceRedraw) {
        setTimeout(() => {
          if (chart && chart.legend) {
            chart.redraw();
          }
        }, 0);
      }

      if (newListSelectedCells.length === 0 && listSelectedCells.length === 0) {
        return listSelectedCells;
      }

      return newListSelectedCells;
    });
  };

  /**
   * Adds every cell inside a ctrl-modified marquee to the existing heat map selection. Scans the series data for non-empty points inside the area that are not already selected, appends them with their counts and templates, restyles them and schedules a redraw, returning the previous list untouched when nothing was added.
   *
   * @param area - Marquee bounds given as xMin, xMax, yMin and yMax.
   * @param chart - Highcharts chart holding the heat map series.
   * @returns Nothing.
   */
  const handleCtrlSelectPoint = (area: any = {}, chart: any) => {
    setListSelectedCells((listSelectedCells: any) => {
      const { data } = chart.series[0];
      const newListSelectedCells: any[] = _.cloneDeep(listSelectedCells);
      let isForceRedraw = false;

      _.forEach(data, (item) => {
        const isInList = _.some(listSelectedCells, { id: item.id });
        const isInArea =
          item.x >= area.xMin &&
          item.x <= area.xMax &&
          item.y >= area.yMin &&
          item.y <= area.yMax &&
          !item.isEmpty;

        if (isInArea && !isInList) {
          isForceRedraw = true;
          newListSelectedCells.push({
            index: item.index,
            id: item.id,
            value: item.value,
            reviewedCount: item.reviewedCount || 0,
            originalReviewedCount: item.originalReviewedCount || 0,
            templates: item.templates || [],
          });
          item.update(formatSelectedPoint(), false);
        }
      });

      if (isForceRedraw) {
        setTimeout(() => {
          if (chart && chart.legend) {
            chart.redraw();
          }
        }, 0);

        return newListSelectedCells;
      }
      return listSelectedCells;
    });
  };

  /**
   * Replaces the heat map selection with the single clicked cell. Restores unselected styling on the previously selected points, styles the clicked one as selected, clears the selection entirely when the cell is empty, and otherwise returns a one element list carrying the cell's counts and templates.
   *
   * @param point - Clicked heat map point.
   * @param chart - Highcharts point context used to restyle cells and trigger the redraw.
   * @returns Nothing.
   */
  const handleNormalClickPoint = (point: any, chart: any) => {
    const { index, id, isEmpty, value } = point;

    setListSelectedCells((listSelectedCells: any) => {
      let isForceRedraw = false;
      let isInList = false;

      _.forEach(listSelectedCells, (item) => {
        if (item.id === id) {
          isInList = true;
          return;
        }
        isForceRedraw = true;
        const point = chart.series.data[item.index];
        point.update(formatUnselectedPoint(), false);
      });

      if (!isInList && !isEmpty) {
        isForceRedraw = true;
        chart.update(formatSelectedPoint(), false);
      }

      if (isForceRedraw) {
        if (chart && chart.series && chart.series.chart) {
          chart.series.chart.redraw();
        }
      }

      if (isEmpty) {
        if (listSelectedCells.length === 0) {
          return listSelectedCells;
        }
        return [];
      }

      if (!isInList || isForceRedraw) {
        return [
          {
            index,
            id,
            value,
            reviewedCount: point.reviewedCount || 0,
            originalReviewedCount: point.originalReviewedCount || 0,
            templates: point.templates || [],
          },
        ];
      }
      return listSelectedCells;
    });
  };

  /**
   * Adds or removes one heat map cell from the selection on a ctrl or meta click. Ignores empty cells, drops the cell and restores unselected styling when it is already selected, and otherwise appends it with its counts and templates and applies the selected styling.
   *
   * @param point - Clicked heat map point.
   * @param chart - Highcharts point context used to restyle the clicked cell.
   * @returns Nothing.
   */
  const handleCtrlClickPoint = (point: any, chart: any) => {
    const { index, id, isEmpty, value, originalReviewedCount, reviewedCount, templates } = point;
    if (isEmpty) {
      return;
    }
    setListSelectedCells((listSelectedCells: any) => {
      const isInList = _.some(listSelectedCells, { id });
      if (isInList) {
        let newListSelectedCells = _.cloneDeep(listSelectedCells);
        newListSelectedCells = _.filter(newListSelectedCells, (item) => item.id !== id);
        chart.update(formatUnselectedPoint());
        return newListSelectedCells;
      }
      chart.update(formatSelectedPoint());
      return [
        ...listSelectedCells,
        {
          index,
          id,
          value,
          originalReviewedCount,
          reviewedCount,
          templates,
        },
      ];
    });
  };

  /**
   * Extends the heat map selection to the rectangle between the last selected cell and the shift-clicked one. Computes the bounding box from the two points, appends every non-empty unselected cell inside it, then pushes the clicked point last so the next shift click anchors on it, and redraws.
   *
   * @param point - Shift-clicked heat map point.
   * @param chart - Highcharts point context whose series data supplies the anchor point.
   * @returns Nothing.
   */
  const handleShiftClickPoint = (point: any, chart: any) => {
    const { data } = chart.series;
    setListSelectedCells((listSelectedCells: any) => {
      const lastestSelectedPoint: any = _.last(listSelectedCells);
      const point2 = data[lastestSelectedPoint.index];
      if (point2.id === point.id) {
        return listSelectedCells;
      }

      const xMax = Math.max(point.x, point2.x);
      const xMin = Math.min(point.x, point2.x);
      const yMax = Math.max(point.y, point2.y);
      const yMin = Math.min(point.y, point2.y);

      const newListSelectedCells = _.cloneDeep(listSelectedCells);
      let isForceRedraw = false;

      _.forEach(data, (item) => {
        if (item.isEmpty) {
          return;
        }

        if (item.id === point2.id) {
          return;
        }

        if (_.some(listSelectedCells, { id: item.id })) {
          return;
        }

        if (item.x >= xMin && item.x <= xMax && item.y >= yMin && item.y <= yMax) {
          if (item.id !== point.id) {
            newListSelectedCells.push({
              index: item.index,
              id: item.id,
              value: item.value,
              reviewedCount: item.reviewedCount || 0,
              originalReviewedCount: item.originalReviewedCount || 0,
              templates: item.templates || [],
            });
          }

          isForceRedraw = true;
          item.update(formatSelectedPoint(), false);
        }
      });

      if (isForceRedraw) {
        // Giữ điểm cuối cùng được chọn luôn luôn nằm ở cuối mảng listSelectedCells
        // Để sau khi tiếp tục nhấn shift + click nó sẽ bắt đầu tính từ điểm đó.
        newListSelectedCells.push({
          id: point.id,
          index: point.index,
          value: point.value,
          reviewedCount: point.reviewedCount || 0,
          originalReviewedCount: point.originalReviewedCount || 0,
          templates: point.templates || [],
        });
        if (chartRef.current && chartRef.current.series) {
          chartRef.current.redraw();
        }
        return newListSelectedCells;
      }
      return listSelectedCells;
    });
  };

  /**
   * Creates the Highcharts heat map or updates the existing one with new data. A later call only refreshes the colour axis scale, switching to logarithmic past a thousand, and the series data; the first call builds the whole configuration with marquee selection, per-point click routing, the custom tooltip and abbreviated axis labels, then emits the heat map data event.
   *
   * @param maxValue - Largest beat count in the data, driving the colour axis type and maximum.
   * @param dataChart - Heat map points to render.
   * @returns Nothing.
   */
  const handleDrawChart = ({ maxValue, dataChart }: { maxValue: any; dataChart: any }) => {
    console.time('LOADTIMEHEATMAP');

    if (chartRef.current) {
      chartRef.current.colorAxis[0].update(
        {
          type: maxValue >= 1000 ? 'logarithmic' : 'linear',
          max: maxValue < 5 ? 5 : null,
        },
        false,
      );
      chartRef.current.series[0].update(
        {
          data: dataChart,
          turboThreshold: dataChart.length,
        },
        true,
      );
      return;
    }
    isLoadedChart.current = false;
    const { COLOR_BREAKS, HEIGHT, XAXIS, YAXIS, GRID_LINE_COLOR, PLOT_HEIGHT } = CHART_INFO;
    // This Highcharts config uses `this`-context callbacks and reads/writes fields (area, point.isEmpty,
    // point.reviewedCount, event.originalEvent.ctrlKey) that @types/highcharts doesn't model - cast to any.
    chartRef.current = Highcharts.chart('heat-map-chart-id', {
      boost: {
        enabled: true,
        useGPUTranslations: true,
        usePreallocated: true,
        seriesThreshold: 1,
      },
      title: null,
      chart: {
        marginRight: 50,
        marginLeft: 22,
        type: 'heatmap',
        style: {
          fontFamily: 'Lato, Arial, Helvetica, sans-serif',
        },
        plotBorderColor: '#DCE1E5',
        plotBorderWidth: 1,
        height: HEIGHT,
        events: {
          /**
           * Runs after every Highcharts heat map redraw. Clears the loading flag.
           *
           * @returns Nothing.
           */
          redraw() {
            setIsLoading(false);
          },
          /**
           * Runs once the Highcharts heat map has finished its first render. Closes the load timer, clears the loading flag and marks the chart loaded after a short delay so the initial redraw does not trigger point updates.
           *
           * @returns Nothing.
           */
          load() {
            console.timeEnd('LOADTIMEHEATMAP');
            setIsLoading(false);
            setTimeout(() => {
              isLoadedChart.current = true;
            }, 20);
            // Without the accessibility module, Highcharts falls back to
            // role="img" + aria-label from the (null) title text — empty
            // here, so the chart has no accessible name. Chart.onload sets
            // that fallback itself right after this handler runs, so
            // override it a tick later.
            const chart = this as unknown as Highcharts.Chart;
            setTimeout(() => {
              chart.renderer.boxWrapper.attr({ 'aria-label': 'RR interval heat map' });
            }, 0);
          },
          /**
           * Handles a marquee drag over the heat map without letting Highcharts zoom. Cancels the default zoom, widens the axis bounds by half a cell, then routes to the ctrl-additive or the replacing selection handler depending on the modifier keys.
           *
           * @param event - Highcharts selection event carrying the axis ranges and the original mouse event.
           * @returns Nothing.
           */
          selection(event: any) {
            // Prevent zooming
            event.preventDefault();

            const chart = this;
            const area = {
              xMin: event.xAxis[0].min - 0.5,
              xMax: event.xAxis[0].max + 0.5,
              yMin: event.yAxis[0].min - 0.5,
              yMax: event.yAxis[0].max + 0.5,
            };

            if (event.originalEvent.ctrlKey || event.originalEvent.metaKey) {
              handleCtrlSelectPoint(area, chart);
              return;
            }

            handleNormalSelectPoint(area, chart);
          },
        },
        // Enable the selection marquee
        zoomType: 'xy',
        animation: false,
      },

      xAxis: {
        min: 0,
        gridLineWidth: 1,
        categories: XAXIS,
        labels: {
          enabled: false,
        },
        title: {
          text: 'RR1 (second)',
          margin: 3,
          style: {
            fontWeight: 700,
            fontSize: '10px',
            color: '#4B5B73',
          },
        },
      },
      yAxis: {
        categories: YAXIS,
        min: 0,
        endOnTick: false,
        labels: {
          enabled: false,
        },
        gridLineColor: GRID_LINE_COLOR,
        showLastLabel: true,
        title: {
          text: 'RR1/RR0 (ratio)',
          margin: 7,
          style: {
            fontWeight: 700,
            fontSize: '10px',
            color: '#4B5B73',
          },
        },
      },
      colorAxis: {
        type: maxValue >= 1000 ? 'logarithmic' : 'linear',
        gridLineColor: GRID_LINE_COLOR,
        min: 1,
        max: maxValue < 5 ? 5 : null,
        allowDecimals: false,
        tickPositions: maxValue >= 5 && maxValue < 10 ? [1, 3, 5, 7, 9] : null,
        stops: [
          [0, COLOR_BREAKS[0]],
          [0.25, COLOR_BREAKS[1]],
          [0.5, COLOR_BREAKS[2]],
          [0.75, COLOR_BREAKS[3]],
          [1, COLOR_BREAKS[4]],
        ],
        reversed: false,
        tickAmount: 5,
        labels: {
          x: 4,
          style: {
            fontSize: '8px',
            fontWeight: 500,
            color: '#4B5B73',
          },
          /**
           * Formats a colour axis tick on the heat map legend. Renders the first tick as 1 and abbreviates every other value into a thousands, millions or billions suffix.
           *
           * @returns The tick label text.
           */
          formatter() {
            if (this.isFirst) {
              return '1';
            }
            return formatNumber(this.value);
          },
        },
      },
      legend: {
        align: 'right',
        verticalAlign: 'top',
        layout: 'vertical',
        margin: 0,
        floating: true,
        symbolHeight: PLOT_HEIGHT,
        symbolWidth: 10,
        y: -8,
        x: 20,
      },
      tooltip: {
        padding: 0,
        hideDelay: 0,
        backgroundColor: '#4B5B73',
        animation: false,
        shape: 'square',
        borderRadius: 4,
        borderWidth: 0,
        useHTML: true,
        className: 'custom-heat-map-chart-tooltip',
        shadow: false,
        style: {
          color: '#ffffff',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '18px',
        },
        /**
         * Builds the heat map tooltip markup for one cell. Suppresses the tooltip on empty cells and otherwise renders the RR1 over RR0 ratio band, the RR1 band and the comma separated quantity and reviewed counts, pluralising the beat noun.
         *
         * @param tooltip - Highcharts tooltip instance, unused here.
         * @returns The tooltip HTML string, or false to suppress the tooltip on an empty cell.
         */
        formatter(tooltip: any) {
          if (this.point.isEmpty) {
            return false;
          }
          // const reviewedHtml = '<div style="margin-top: 4px" class=\'icon-reviewd\'>Reviewed</div>';
          const count = this.point?.reviewedCount ? this.point.reviewedCount : 0;
          const { x, y, value } = this.point;
          return `<div style="width: 180px; padding: 4px 6px;" class='tooltip-wrapper'>
                        <div>RR1/RR0: ${YAXIS[y]}</div>
                        <div style="margin-top: 4px">RR1: ${XAXIS[x]}</div>
                        <div style="margin-top: 4px">Quantity: ${numberWithCommas(value)} ${value > 1 ? 'beats' : 'beat'}</div>
                        <div style="margin-top: 4px">Reviewed: ${numberWithCommas(count)} ${count > 1 ? 'beats' : 'beat'}</div>
                      </div> `;
        },
      },
      series: [
        {
          data: dataChart,
          animation: false,
          dataLabels: {
            enabled: true,
            verticalAlign: 'middle',
            style: {
              fontSize: '0px', // Still have data label text but hide it to support automation test
            },
            /**
             * Formats the data label drawn inside a heat map cell. Returns null for empty cells and otherwise the raw point value, deliberately left unabbreviated so automation tests can read it.
             *
             * @returns The cell's beat count, or null for an empty cell.
             */
            formatter() {
              if (this.point?.isEmpty) {
                return null;
              }
              // not using formatNumber for automation test formatNumber(this.point.value)
              return this.point.value;
            },
          },
          boostThreshold: 1,
          turboThreshold: dataChart.length,
          borderColor: GRID_LINE_COLOR,
          borderWidth: 1,
          states: {
            hover: {
              enabled: true,
              lineColor: '#85B3F2',
              brightness: -0.1,
            },
          },
          point: {
            events: {
              /**
               * Routes a click on a heat map cell to the matching selection behaviour. Sends ctrl or meta clicks to the additive handler, shift clicks to the range handler and everything else to the replacing handler.
               *
               * @param event - Highcharts point click event carrying the point and the modifier keys.
               * @returns Nothing.
               */
              click(event: any) {
                const chart = this;
                if (event.ctrlKey || event.metaKey) {
                  handleCtrlClickPoint(event.point, chart);
                  return;
                }
                if (event.shiftKey) {
                  handleShiftClickPoint(event.point, chart);
                  return;
                }
                handleNormalClickPoint(event.point, chart);
              },
            },
          },
        },
      ],
      plotOptions: {
        series: {
          animation: false,
        },
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              yAxis: {
                labels: {
                  format: '{substr value 0 1}',
                },
              },
            },
          },
        ],
      },
      exporting: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
    } as any);
    emitter.emit(EMITTER_CONSTANTS.UPDATE_HEATMAP_CHART_DATA);
  };

  /**
   * Clears the heat map selection when ESC is pressed over the beats tab. Skips auto-repeat, any press while the tab zone is not hovered and any press with ctrl or meta held, then restores unselected styling on every selected point, redraws and empties the selection.
   *
   * @param event - Keyboard event from the window listener.
   * @returns Nothing.
   */
  const handleKeyDown = (event: any) => {
    if (event?.repeat) {
      return;
    }
    const tabZone = document.getElementById(BEAT_HR_TAB_ID);
    // *:Active key press when hover in tab zone
    if (tabZone && !tabZone.matches(':hover')) {
      return;
    }
    const { keyCode, ctrlKey, metaKey } = event;
    if (ctrlKey || metaKey) {
      return;
    }
    if (keyCode === 27) {
      // ESC
      setListSelectedCells((listSelectedCells) => {
        if (_.isEmpty(listSelectedCells)) {
          return listSelectedCells;
        }
        _.forEach(listSelectedCells, (item) => {
          const point = chartRef.current.series[0].data[item.index];
          point.update(formatUnselectedPoint(), false);
        });
        if (chartRef.current) {
          chartRef.current.redraw();
        }
        return [];
      });
    }
  };

  /**
   * Re-anchors the heat map selection onto freshly rebuilt chart data. Looks each previously selected cell up by id among the new points and keeps only those still present, refreshing their index, value, reviewed counts and templates.
   *
   * @param dataChart - Newly built heat map points.
   * @returns Nothing.
   */
  const handleReSelectCells = (dataChart: any) => {
    setListSelectedCells((prev: any) => {
      if (_.isEmpty(prev)) return prev;
      const newListSelectedCells: any[] = [];
      _.forEach(prev, (item) => {
        const point = _.find(dataChart, { id: item.id });
        if (point) {
          newListSelectedCells.push({
            index: point.index,
            id: point.id,
            value: point.value,
            reviewedCount: point.reviewedCount || 0,
            originalReviewedCount: point.originalReviewedCount || 0,
            templates: point.templates || [],
          });
        }
      });
      return newListSelectedCells;
    });
  };

  useEmitter(
    EMITTER_CONSTANTS.UPDATE_HEATMAP_CHART_DATA,
    () => {
      setIsLoading(true);
      const listSelectedCells = getListSelectedCells();
      const { dataChart, maxValue } = formatDataChart({
        currentDataHeatmapChart,
        isHideReviewedCell,
        listSelectedCells,
        type: activeButton,
        resetBulkReviewedBeats: getResetBulkReviewedBeats(),
        beatReviewChanges: getReviewedBeatsChanges(),
        bulkReviewedBeats: getBulkReviewedBeats(),
      });
      setTimeout(() => {
        handleDrawChart({
          maxValue,
          dataChart,
        });
      }, 20);
      handleReSelectCells(dataChart);
    },
    [currentDataHeatmapChart, isHideReviewedCell, activeButton],
  );

  useEffect(() => {
    setIsLoading(true);
    const data = getDataHeatmapChart();
    const currentData = (data as any)[activeButton];
    const { dataChart, maxValue } = formatDataChart({
      currentDataHeatmapChart: currentData,
      isHideReviewedCell: getIsHideReviewedCell(),
      type: activeButton,
      resetBulkReviewedBeats: getResetBulkReviewedBeats(),
      beatReviewChanges: getReviewedBeatsChanges(),
      bulkReviewedBeats: getBulkReviewedBeats(),
    });
    setTimeout(() => {
      handleDrawChart({
        maxValue,
        dataChart,
      });
    }, 20);
  }, [activeButton]);

  useEffect(
    () => () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useUpdateEffect(() => {
    if (!isLoadedChart.current || !chartRef.current) {
      return;
    }
    const { data } = chartRef.current.series[0];

    if (!data) {
      return;
    }
    let isForceRedraw = false;
    const newListSelectedCells = _.cloneDeep(getListSelectedCells());

    _.forEach(data, (point) => {
      if (!point.value) {
        return;
      }
      const filterBeatChanges = _.filter(reviewedBeatsChanges, (x) => x.cellId === point.id);
      const { isReviewed, newReviewedCount } = calculateReviewedStatus({
        resetBulkReviewedBeats,
        item: point,
        filterBeatChanges,
        bulkReviewedBeats,
      });
      point.reviewedCount = newReviewedCount;
      if (isReviewed) {
        if (isHideReviewedCell) {
          point.update(formatEmptyPoint(), false);
          _.remove(newListSelectedCells, (x) => x.id === point.id);
        } else {
          point.update(formatReviewedPoint({}, true), false);
        }
      } else {
        point.update(formatReviewedPoint({}, false), false);
      }
      isForceRedraw = true;
    });

    if (chartRef.current && isForceRedraw) {
      chartRef.current.redraw();
    }
    if (isHideReviewedCell) {
      setListSelectedCells((prev) => {
        if (prev.length === newListSelectedCells.length) {
          return prev;
        }
        return newListSelectedCells;
      });
    }
  }, [bulkReviewedBeats, isHideReviewedCell, reviewedBeatsChanges, resetBulkReviewedBeats]);

  useEmitter(
    EMITTER_CONSTANTS.REMOVE_SELECTED_CELLS,
    () => {
      const listCells = getListSelectedCells();
      if (_.isEmpty(listCells)) {
        return;
      }

      setListSelectedCells((listSelectedCells) => {
        if (_.isEmpty(listSelectedCells)) {
          return listSelectedCells;
        }
        _.forEach(listSelectedCells, (item) => {
          const point = chartRef.current?.series?.[0]?.data?.[item?.index];
          if (point) {
            point.update(formatUnselectedPoint(), false);
          }
        });
        return [];
      });

      setTimeout(() => {
        if (chartRef.current && chartRef.current.legend) {
          chartRef.current.redraw();
        }
      }, 0);
    },
    [chartRef.current],
  );

  return {
    isLoading,
  };
};

export default useHeatMapChart;
